import { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, ClipboardCheck, Loader2, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import apiClient from "@/lib/api-client";
import { Field } from "@/components/forms";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface SettingField {
  key: string;
  value: string;
  secret: boolean;
  is_set: boolean;
  source: "database" | "environment" | "unset";
  error?: string;
}

interface SettingsCheck {
  key: string;
  label: string;
  ok: boolean;
  message: string;
}

const FIELD_INFO: Record<string, { label: string; help: string; placeholder?: string }> = {
  // Stored under its original name; it now holds the Snippe API key.
  snippe_link_key: {
    label: "Snippe API Key",
    help: "The key named “Taskpart API” in Snippe Dashboard → Settings → API Keys. It starts with snp_.",
    placeholder: "snp_…",
  },
  snippe_webhook_secret: {
    label: "Webhook Secret",
    help: "Optional. From Snippe Dashboard → Settings → Webhook Secret, to verify payment notifications.",
  },
  snippe_webhook_url: {
    label: "Webhook URL",
    help: "Filled in automatically when you save. This API's public callback URL.",
    placeholder: "https://api.taskpart.org/api/v1/payment/callback",
  },
  frontend_url: {
    label: "Frontend URL",
    help: "Filled in automatically when you save. Where students return after paying.",
    placeholder: "https://taskpart.org",
  },
  snippe_base_url: {
    label: "Snippe API URL",
    help: "Leave as https://api.snippe.sh unless Snippe tells you otherwise.",
    placeholder: "https://api.snippe.sh",
  },
  snippe_profile_id: {
    label: "Payment Profile ID (optional)",
    help: "Branding profile from Snippe Dashboard → Settings → Payment Profiles.",
  },
};

// The only setting an admin has to provide; the rest are optional.
const PRIMARY_KEY = "snippe_link_key";

const SOURCE_LABEL: Record<SettingField["source"], string> = {
  database: "Saved here",
  environment: "From server env",
  unset: "Not set",
};

export default function PaymentGatewaySettings() {
  const [fields, setFields] = useState<SettingField[]>([]);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [clear, setClear] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [checks, setChecks] = useState<SettingsCheck[] | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const applyFields = (next: SettingField[]) => {
    setFields(next);
    // Secrets start empty: the page never holds them, and an empty secret
    // means "keep the saved one".
    setDraft(
      Object.fromEntries(
        next.map((f) => [f.key, f.secret || f.source !== "database" ? "" : f.value]),
      ),
    );
    setClear([]);
  };

  useEffect(() => {
    apiClient
      .get("/admin/payments/settings")
      .then((res) => applyFields(res.data?.data ?? []))
      .catch(() => toast.error("Couldn't load payment settings."))
      .finally(() => setLoading(false));
  }, []);

  const isDirty =
    clear.length > 0 ||
    fields.some((f) => {
      const v = (draft[f.key] ?? "").trim();
      if (f.secret) return v !== "";
      return v !== (f.source === "database" ? f.value : "");
    });

  const handleSave = async () => {
    setSaving(true);
    try {
      const values: Record<string, string> = {};
      for (const f of fields) {
        const v = (draft[f.key] ?? "").trim();
        if (f.secret) {
          if (v) values[f.key] = v;
        } else if (v !== (f.source === "database" ? f.value : "")) {
          values[f.key] = v;
        }
      }
      const res = await apiClient.put("/admin/payments/settings", { values, clear });
      applyFields(res.data?.data ?? []);
      setChecks(null);
      toast.success("Payment settings saved.");
    } catch (err: any) {
      toast.error("Couldn't save payment settings", {
        description: err?.response?.data?.error || err?.response?.data?.message,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (isDirty) {
      toast.info("Save your changes first, then test them.");
      return;
    }
    setTesting(true);
    try {
      const res = await apiClient.post("/admin/payments/settings/test");
      const result: { ok: boolean; checks: SettingsCheck[] } = res.data?.data;
      setChecks(result.checks);
      if (result.ok) {
        toast.success("Snippe is set up correctly", {
          description: "Snippe accepted the API key. Students can now pay online.",
        });
      } else {
        const failed = result.checks.filter((c) => !c.ok);
        toast.error(
          `${failed.length} payment setting${failed.length === 1 ? "" : "s"} need attention`,
          {
            description: failed.map((c) => `${c.label}: ${c.message}`).join(" "),
          },
        );
      }
    } catch (err: any) {
      toast.error("Couldn't run the payment settings test", {
        description: err?.response?.data?.message,
      });
    } finally {
      setTesting(false);
    }
  };

  const renderField = (f: SettingField, wide = false) => {
    const info = FIELD_INFO[f.key] ?? { label: f.key, help: "" };
    const cleared = clear.includes(f.key);
    return (
      <div key={f.key} className={cn("flex flex-col gap-1.5", wide && "md:col-span-2")}>
        <Field
          label={info.label}
          labelAction={
            <Badge variant={cleared ? "warning" : f.is_set ? "success" : "outline"}>
              {cleared ? "Will be removed" : SOURCE_LABEL[f.source]}
            </Badge>
          }
          error={f.error ? `${f.error} Enter it again.` : undefined}
          hint={info.help || undefined}
        >
          <Input
            type={f.secret ? "password" : "text"}
            autoComplete="off"
            className={f.secret ? "font-mono" : undefined}
            value={draft[f.key] ?? ""}
            placeholder={
              f.secret && f.is_set ? `${f.value} (saved, leave blank to keep)` : info.placeholder
            }
            onChange={(e) => {
              setDraft({ ...draft, [f.key]: e.target.value });
              if (cleared) setClear(clear.filter((k) => k !== f.key));
            }}
          />
        </Field>
        {f.secret && f.source === "database" && !cleared && (
          <Button
            variant="ghost"
            size="sm"
            className="self-start text-muted-foreground hover:text-foreground"
            onClick={() => {
              setClear([...clear, f.key]);
              setDraft({ ...draft, [f.key]: "" });
            }}
          >
            <Trash2 />
            Remove saved value
          </Button>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12" role="status" aria-label="Loading payment settings">
        <Loader2 className="size-6 animate-spin text-muted-foreground" aria-hidden />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-title text-foreground">Payment Gateway (Snippe)</h2>
        <p className="mt-1 max-w-2xl text-sm leading-6 text-body">
          Paste your Snippe API key, save, then run the test. Keys are stored encrypted and only
          shown masked; leave a key blank to keep the saved one.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {fields.filter((f) => f.key === PRIMARY_KEY).map((f) => renderField(f, true))}
      </div>

      <div>
        <Button
          variant="ghost"
          size="sm"
          className="-ml-3 text-muted-foreground hover:text-foreground"
          aria-expanded={showAdvanced}
          onClick={() => setShowAdvanced(!showAdvanced)}
        >
          {showAdvanced ? "Hide advanced settings" : "Advanced settings (optional)"}
          {showAdvanced ? <ChevronUp /> : <ChevronDown />}
        </Button>
        {showAdvanced && (
          <div className="mt-4 grid grid-cols-1 gap-6 md:grid-cols-2">
            {fields.filter((f) => f.key !== PRIMARY_KEY).map((f) => renderField(f))}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2 sm:flex-row">
        <Button disabled={!isDirty || saving} loading={saving} onClick={handleSave}>
          {!saving && <Save />}
          Save payment settings
        </Button>
        <Button variant="outline" disabled={testing} loading={testing} onClick={handleTest}>
          {!testing && <ClipboardCheck />}
          Test settings
        </Button>
      </div>

      {checks && (
        <ul className="divide-y divide-border rounded-md border border-border bg-card">
          {checks.map((c) => (
            <li key={c.key} className="flex items-start gap-3 px-4 py-3">
              <Badge variant={c.ok ? "success" : "destructive"} dot className="min-w-11 justify-center">
                {c.ok ? "OK" : "Fix"}
              </Badge>
              <p className="text-sm text-body">
                <span className="font-medium text-foreground">{c.label}:</span> {c.message}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
